import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SortDirection } from "../../interfaces";
import { Holder } from "../../interfaces/Holder";
import { holderSelectors } from "../../redux";
import { loadAllHolders } from "../../redux/holder/sagaActions";

interface Column {
  id: "email" | "displayName" | "phoneNumber" | "cityId";
  label: string;
  minWidth?: number;
  align?: "right";
  format?: (value: number) => string;
}

const columns: Column[] = [
  { id: "email", label: "Email", minWidth: 100 },
  {
    id: "displayName",
    label: "Nome",
    minWidth: 100,
    align: "right",
  },

  {
    id: "phoneNumber",
    label: "Numero di telefono",
    minWidth: 100,
    align: "right",
  },
];

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  container: {
    maxHeight: 440,
  },
  addButton: {
    marginTop: 20,
  },
});

interface TableHolderProps {
  onEditHolder: (holder: Holder | undefined) => void;
  onDeleteHolder: (id: string) => void;
}

const TableHolder: React.FC<TableHolderProps> = ({
  onDeleteHolder,
  onEditHolder,
}) => {
  const holders = useSelector(holderSelectors.getAllHolders);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const getHolderById = useSelector(holderSelectors.getHolderById);

  const maxPage = Number(holders.data?.totalCount);

  const handleSelectHolder = (id: string) => {
    onEditHolder(getHolderById(id));
  };

  useEffect(() => {
    dispatch(loadAllHolders({}));
  }, [dispatch]);

  const handleChangePage = (event: unknown, newPage: number) => {
    if (newPage > page) {
      dispatch(
        loadAllHolders({
          start_after:
            holders.data?.results[holders.data?.results.length - 1].uid,
          sort_by: [{ field: "uid", order: SortDirection.ASC }],
        })
      );
    } else {
      dispatch(
        loadAllHolders({
          end_before:
            holders.data?.results[holders.data?.results.length - 1].uid,
          sort_by: [{ field: "uid", order: SortDirection.ASC }],
        })
      );
    }
    setPage(0);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const deleteHolder = (id: string) => {
    onDeleteHolder(id);
  };

  if (!holders.data || holders.data?.results.length === 0) return null;

  return (
    <>
      <Paper className={classes.root}>
        <TableContainer className={classes.container}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell align="right">Modifica</TableCell>
                <TableCell align="right">Cancella</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {holders.data?.results.map((row) => {
                return row ? (
                  <TableRow hover role="checkbox" tabIndex={-1} key={row?.uid}>
                    {columns.map((column, index) => {
                      const value = row[column.id];
                      return (
                        <React.Fragment key={column.id + index}>
                          <TableCell align={column.align}>{value}</TableCell>
                        </React.Fragment>
                      );
                    })}
                    <TableCell align="right">
                      <Tooltip title="Modifica">
                        <IconButton
                          aria-label="edit"
                          onClick={() => handleSelectHolder(row?.uid)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Elimina">
                        <IconButton
                          aria-label="delete"
                          onClick={() => deleteHolder(row?.uid)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ) : null;
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={maxPage}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage="N. elementi"
        />
      </Paper>
    </>
  );
};

export default React.memo(TableHolder);
